import { AppBar } from "@/elements/components/ui/app-bar";
import { Button } from "@/elements/components/ui/button";
import { Input } from "@/elements/components/ui/input";
import { Redirect, useRouter } from "expo-router";
import React, { useMemo } from "react";
import { ScrollView, Text, View } from "react-native";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuthActions } from "@convex-dev/auth/react";
import { makeRedirectUri } from "expo-auth-session";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { auth } from "@/elements/components/utils/convex-api";
import GoogleIcon from "@/assets/svgs/google-icon";
import AsyncStorage from "@react-native-async-storage/async-storage";
import Loader from "@/elements/components/shared/loader";
import ResponsiveContent from "@/elements/components/shared/responsive-content";

export const StudentCheckSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

type StudentCheckSchemaType = z.infer<typeof StudentCheckSchema>;

function StudentCheckScreen() {
  const router = useRouter();
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm({
    defaultValues: {
      email: "",
    },
    mode: "onChange",
    resolver: zodResolver(StudentCheckSchema),
  });
  const { signIn: authorize } = useAuthActions();

  const redirectTo = useMemo(
    () =>
      makeRedirectUri({
        scheme: "teachloop",
        path: "sign-in",
        queryParams: {
          persona: "student",
        },
      }),
    []
  );

  const { data: isAuthenticated = false, isSuccess } = useQuery(
    convexQuery(auth.isAuthenticated, {})
  );

  if (!isSuccess) {
    return <Loader />;
  }

  if (isAuthenticated) {
    return <Redirect href={"/(app)/home"} />;
  }

  const onSubmit = async (data: StudentCheckSchemaType) => {
    router.navigate(`/(auth)/student/create-password?email=${data.email}`);
  };

  const onGoogleSignIn = async () => {
    await AsyncStorage.setItem("persona", "student");
    await authorize("google", {
      redirectTo: redirectTo,
    });
  };

  const onAppleSignIn = async () => {
    await AsyncStorage.setItem("persona", "student");
    await authorize("apple", {
      redirectTo: redirectTo,
    });
  };

  return (
    <View className="flex-1 bg-teachLoop-creamLight">
      <AppBar>
        <AppBar.Icon
          onPress={() =>
            router.canGoBack()
              ? router.back()
              : router.replace("/(auth)/sign-in-options")
          }
        />
        <AppBar.Title>Back</AppBar.Title>
      </AppBar>
      <ScrollView showsVerticalScrollIndicator={false}>
        <ResponsiveContent>
          <View className="mx-5">
            <View className="h-4" />
            <View className="gap-1">
              <Text className="font-zilla-slab-medium leading-[1.2] font-medium text-[28px] text-black">
                Let&apos;s start with your email
              </Text>
              <Text className="font-geologica-regular font-normal text-[20px] text-black leading-[1.5] ">
                Enter the address provided by your parent.
              </Text>
            </View>
            <View className="h-8" />
            <View className="gap-4">
              <Controller
                control={control}
                name="email"
                render={({ field, fieldState }) => (
                  <Input
                    size="md"
                    placeholder="Email"
                    {...field}
                    error={!!fieldState.error?.message}
                    keyboardType="email-address"
                    autoCapitalize="none"
                  />
                )}
              />
            </View>
            <View className="h-8" />
            <Button
              className="w-full"
              size="md"
              intent="primary"
              onPress={handleSubmit(onSubmit)}
              disabled={!isValid}
            >
              <Button.Text size="md">Next</Button.Text>
            </Button>
            <View className="h-8" />
            {/* <View className="flex-row items-center gap-4 px-[27px]">
              <View className="flex-1 h-[1] bg-gray-300" />
              <Text className="text-teachLoop-textPrimary text-[16px] font-geologica-medium font-medium">
                Or
              </Text>
              <View className="flex-1 h-[1] bg-gray-300" />
            </View>
            <View className="h-8" /> */}
            {/* <View className="gap-4">
              <Button
                className="w-full"
                size="md"
                intent="primary"
                outline
                onPress={onGoogleSignIn}
                icon
              >
                <Button.Icon
                  as={GoogleIcon}
                  intent="primary"
                  className="size-5"
                />
                <Button.Text size="md" intent="primary">
                  With Google
                </Button.Text>
              </Button>
            </View>
            <View className="h-8" /> */}
          </View>
        </ResponsiveContent>
      </ScrollView>
    </View>
  );
}
export default StudentCheckScreen;
